<script lang="ts">
  import { onMount } from 'svelte';
  import { push } from 'svelte-spa-router';
  import { AuthService } from '../../lib/api/auth.service';
  import { userStore } from '../../stores/auth.store';
  import AuthPageLayout from '../../components/auth/AuthPageLayout.svelte';

  export let params: { token?: string } = {};

  let verificationToken = '';
  let isLoading = true;
  let verificationState: 'loading' | 'success' | 'error' | 'missing' = 'loading';
  let errorMessage = '';
  let newEmail = '';

  onMount(async () => {
    const urlParams = new URLSearchParams(window.location.search);
    verificationToken = params.token || urlParams.get('token') || '';

    if (!verificationToken) {
      verificationState = 'missing';
      isLoading = false;
      return;
    }

    try {
      const result = await AuthService.confirmEmailUpdate({ token: verificationToken });

      if (result.success) {
        verificationState = 'success';
        newEmail = result.user?.email || '';
        if (result.user) {
          userStore.set(result.user);
        }
      } else {
        verificationState = 'error';
        const apiMessage = (result.error as { response?: { data?: { message?: string } } })?.response?.data?.message;
        errorMessage = apiMessage || 'Failed to verify your new email address. The link may have expired.';
      }
    } catch (error: unknown) {
      verificationState = 'error';
      const apiMessage = (error as { response?: { data?: { message?: string } } })?.response?.data?.message;
      errorMessage = apiMessage || 'Failed to verify your new email address. The link may have expired.';
    } finally {
      isLoading = false;
    }
  });

  function goToLogin() {
    push('/login');
  }

  function goToDashboard() {
    push('/dashboard');
  }
</script>

<svelte:head>
  <title>Verify Email Change - Leave2Give</title>
</svelte:head>

{#if isLoading || verificationState === 'loading'}
  <AuthPageLayout title="Verify email change" subtitle="Confirming your new sign-in email...">
    <div class="auth-loading">
      <div class="auth-spinner" aria-hidden="true"></div>
      <p>Please wait a moment</p>
    </div>
  </AuthPageLayout>
{:else if verificationState === 'success'}
  <AuthPageLayout title="Email updated" subtitle="Your sign-in email has been changed.">
    <div class="auth-status-panel">
      <svg
        width="56"
        height="56"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        class="auth-status-icon success"
        aria-hidden="true"
      >
        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
        <polyline points="22 4 12 14.01 9 11.01"></polyline>
      </svg>
      <p>
        {#if newEmail}
          You can now sign in with <strong>{newEmail}</strong>.
        {:else}
          Your new email address is active. Use it the next time you sign in.
        {/if}
      </p>
      <div class="auth-actions">
        <button type="button" class="auth-btn" on:click={goToLogin}>
          Go to sign in
        </button>
        {#if $userStore}
          <button type="button" class="auth-btn-secondary" on:click={goToDashboard}>
            Go to dashboard
          </button>
        {/if}
      </div>
    </div>
  </AuthPageLayout>
{:else}
  <AuthPageLayout
    title="Could not verify email"
    subtitle={verificationState === 'missing' ? 'This verification link is not valid.' : 'This verification link could not be used.'}
  >
    <div class="auth-status-panel">
      <svg
        width="56"
        height="56"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        class="auth-status-icon error"
        aria-hidden="true"
      >
        <circle cx="12" cy="12" r="10"></circle>
        <line x1="15" y1="9" x2="9" y2="15"></line>
        <line x1="9" y1="9" x2="15" y2="15"></line>
      </svg>
      <p>
        {verificationState === 'missing'
          ? 'The link may be incomplete. Request a new email change from your profile settings.'
          : errorMessage}
      </p>
      <div class="auth-actions">
        <button type="button" class="auth-btn" on:click={goToLogin}>
          Go to sign in
        </button>
      </div>
    </div>
  </AuthPageLayout>
{/if}
